import { storage } from "../lib/storage";

export type Json = Record<string, unknown>;

const API_BASE = import.meta.env.VITE_API_URL || "https://api.lotin.uz/api";
const API_PROXY_BASE = "/api";

function withProxyBase(path: string): string {
  return `${API_PROXY_BASE}${path.startsWith("/") ? path : `/${path}`}`;
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  if (typeof window !== "undefined") {
    const token = storage.getItem("auth_token");
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }
  }
  return headers;
}

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

// Build a rich error from a failed Response: prefer the backend's JSON
// message/error over a bare "HTTP <code>", and attach status + body so
// callers can localize (e.g. 401 -> "invalid credentials").
async function apiError(res: Response): Promise<ApiError> {
  let body: any = null;
  try {
    body = await res.json();
  } catch {
    /* non-JSON body */
  }
  // Unwrap a Laravel validation payload ({ errors: { field: [msg] } }) to its
  // first message so the UI shows something human, never a raw "HTTP 422".
  let firstValidation: string | undefined;
  if (body && typeof body.errors === "object" && body.errors) {
    const first = Object.values(body.errors as Record<string, string[]>)[0];
    if (Array.isArray(first)) firstValidation = first[0];
  }

  const err = new Error(
    (body && (body.message || body.error)) ||
      firstValidation ||
      `HTTP ${res.status}`,
  ) as ApiError;
  err.status = res.status;
  err.body = body;
  return err;
}

export async function postJson<T>(
  path: string,
  body: Json,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    credentials: init?.credentials ?? "omit",
    body: JSON.stringify(body),
    ...init,
    headers: { "Content-Type": "application/json", ...(init?.headers || {}) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

// Direct API call
export async function postApiJson<T>(
  path: string,
  body: Json,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(withProxyBase(path), {
    method: "POST",
    body: JSON.stringify(body),
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as T;
}

// Direct API call with PUT method
export async function putApiJson<T>(
  path: string,
  body: Json,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(withProxyBase(path), {
    method: "PUT",
    body: JSON.stringify(body),
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as T;
}

export async function postForm<T>(
  path: string,
  form: FormData,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    body: form,
    credentials: init?.credentials ?? "omit",
    ...init,
  });
  if (!res.ok) {
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await res.json();
      const err = new Error(
        (data && (data.message as string)) || `HTTP ${res.status}`,
      );
      (err as any).status = res.status;
      (err as any).body = data; // carries backend codes e.g. { error: "subscription_required" }
      throw err;
    }
    const text = await res.text();
    const err = new Error(text || `HTTP ${res.status}`);
    (err as any).status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

export async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    credentials: init?.credentials ?? "omit",
    ...init,
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

// Direct API call
export async function postApiForm<T>(
  path: string,
  form: FormData,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(withProxyBase(path), {
    method: "POST",
    body: form,
    credentials: "include",
    ...init,
    // headers merged LAST so a caller's init.headers can't drop the auth header
    // (spreading ...init after headers would overwrite the whole headers object).
    headers: {
      ...getAuthHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const contentType = res.headers.get("content-type") || "";
    if (contentType.includes("application/json")) {
      const data = await res.json();
      const err = new Error(
        (data && (data.message as string)) || `HTTP ${res.status}`,
      );
      (err as any).status = res.status;
      (err as any).body = data; // carries backend codes e.g. { error: "subscription_required" }
      throw err;
    }
    const text = await res.text();
    const err = new Error(text || `HTTP ${res.status}`);
    (err as any).status = res.status;
    throw err;
  }
  return (await res.json()) as T;
}

// Direct API call
export async function getApiJson<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(withProxyBase(path), {
    method: "GET",
    credentials: "include",
    ...init,
    headers: {
      ...getAuthHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) throw await apiError(res);
  return (await res.json()) as T;
}

// Direct API call with DELETE method (optional JSON body)
export async function deleteApiJson<T>(
  path: string,
  body?: Json,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(withProxyBase(path), {
    method: "DELETE",
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => null);
    const err = new Error(
      (data && (data.message as string)) || `HTTP ${res.status}`,
    );
    (err as any).status = res.status;
    (err as any).body = data;
    throw err;
  }
  return (await res.json()) as T;
}
